import { cva } from 'class-variance-authority';

import { focusRing } from '../styles/focusRing';

/**
 * Inline link class recipe.
 *
 * Direction C from the 2026-10-05 exploration (`.context/decision-text-link.md`): the link inherits
 * the colour of the sentence it sits in and is always underlined. On hover the text and its underline
 * step together to the hover step of the colour role the sentence already uses, and the line thickens
 * to 2px. A surface that colours its text names that step in `--ds-text-link-hover` and
 * `--ds-text-link-active` (an Alert does, per severity: red text goes to `destructive/hover`). With no
 * slot set the sentence is `foreground/primary` and the step is `foreground/primary/hover`.
 *
 * Why not accent: against `foreground/primary` the accent measures 2.1:1 in light and 1.7:1 in
 * dark, so colour alone fails WCAG 1.4.1; and inside an Alert the body text IS the severity colour,
 * where the accent measures 1.0 to 1.1:1. Inheriting is the one treatment that is right on every
 * surface without a per-surface rule. The underline is the cue.
 *
 * ── Motion ────────────────────────────────────────────────────────────────────────────────────────
 *
 * Colour and underline thickness on `--motion-control`, the role a control's own tint reads. Neither
 * moves layout. No press scale: this is text inside a sentence, and scaling it would shift the
 * words beside it. No entrance, because a link never appears on its own.
 *
 * `hover:` is Tailwind's own variant, which v4 wraps in `@media (hover: hover)`, so a tap on a touch
 * device does not leave the accent stuck on. Focus drops the duration to 0 so the ring is not late.
 *
 * ── The app's global anchor rule ──────────────────────────────────────────────────────────────────
 *
 * `apps/frontend/src/index.css` paints every `a` link-blue in an UNLAYERED rule, which beats the
 * `text-inherit` utility. `ds-text-link` is how that rule excludes this component, the same way it
 * excludes `ds-btn-*`. Rename the class and the exclusion must move with it.
 *
 * `[box-decoration-break:clone]` gives every wrapped line fragment its own focus ring instead of one
 * ring broken across the line wrap.
 */
export const textLinkRecipe = cva(
  [
    'ds-text-link',
    'rounded-sm text-inherit underline decoration-1 underline-offset-2 [box-decoration-break:clone]',
    'transition-[color,text-decoration-thickness] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
    'hover:text-[var(--ds-text-link-hover,var(--foreground-primary-hover))] hover:decoration-2',
    'active:text-[var(--ds-text-link-active,var(--foreground-primary-hover))]',
    'focus-visible:[transition-duration:0s]',
    focusRing(),
  ],
  {
    variants: {
      /**
       * A line of its own rather than a phrase in a sentence.
       *
       * Draws nothing new. `inline-flex` is what lets a 16px icon sit beside the label and gives the
       * label its own box, and the reset is for `render={<button />}`, which would otherwise bring a
       * grey face, a border and the browser's 13px font. The font reset is wrapped in `:where()` so it has
       * no specificity: a type utility from the caller (`text-label-lg`) always wins, in either order.
       * `font-[inherit]` would not do this job, it sets the family only. All of it is a no-op on an anchor.
       *
       * THE TAP AREA. A text line is about 20px tall, under WCAG 2.5.8's 24px floor, and the standing
       * exception for inline links does not cover a link that has a line to itself. The `::before` is
       * invisible and centred: 24px tall everywhere, 44px on a phone (the house floor, the same
       * invisible target `Button` draws), and never narrower than 24px. Two standalone links on one
       * line must keep their label boxes apart by the overlap, or the areas collide.
       */
      standalone: {
        true: [
          'relative inline-flex items-center gap-1 appearance-none border-0 bg-transparent p-0 cursor-pointer text-start [:where(&)]:[font:inherit]',
          'disabled:cursor-default disabled:text-[var(--foreground-disabled)] disabled:hover:text-[var(--foreground-disabled)] disabled:active:text-[var(--foreground-disabled)] disabled:hover:decoration-1',
          "before:absolute before:top-1/2 before:left-1/2 before:h-6 before:w-[max(100%,--spacing(6))] before:-translate-1/2 before:content-[''] max-sm:before:h-11",
        ],
        false: '',
      },
      /**
       * The accent is wayfinding, and it is only offered on a standalone action, which the types
       * enforce. It reads the same two slots a surface sets for its own severity, so a link inside an
       * Alert still inherits and never reaches this branch.
       *
       * 9.1:1 on white, 8.7:1 on the page, 7.6:1 on `background/secondary`; 12.6 / 11.5 / 10.6 in dark.
       */
      appearance: {
        inherit: '',
        accent: [
          'text-[var(--foreground-accent)]',
          '[--ds-text-link-hover:var(--foreground-accent-hover)] [--ds-text-link-active:var(--foreground-accent-active)]',
        ],
      },
    },
    defaultVariants: { standalone: false, appearance: 'inherit' },
  }
);

/**
 * The 16px icon box beside a standalone label.
 *
 * `text-[length:…]` as well as the size, for the reason `buttonIconRecipe` records: the MUI-backed
 * icons size themselves `1em`, and without a font size here that resolved to the label's.
 */
export const textLinkIconRecipe = cva(
  'inline-flex size-4 shrink-0 items-center justify-center text-[length:--spacing(4)] [&_svg]:size-full'
);
